import { cn } from '@/lib/utils';
import {
  BlockMarkdown,
  InlineMarkdown,
} from '@/components/core/markdown/markdown';
import { VisualBlock } from '@/components/core/visual';
import type { Visual } from '@/types/visual';
import { LEADING, TYPE } from './typography';

interface BodyContentProps {
  body: string;
  className?: string;
  style?: React.CSSProperties;
  // Optional override for list-item / paragraph spacing
  spacing?: string;
  // The card's visual (summarise view), drawn above the text.
  visual?: Visual | null;
}

// A table row or display maths spans lines too, like a fence.
const BLOCK_BODY = /```|^\s*\|.*\|\s*$|\$\$/m;

// Renders card.body either as a bulleted list (when every non-empty
// line starts with -, *, or •) or as paragraphs. Used by every template
// so bullet rendering rules stay consistent.
export function BodyContent({ visual, ...props }: BodyContentProps) {
  if (!visual) return <BodyText {...props} />;
  return (
    <div className='space-y-4'>
      {/* The card is drawn at 800px and shrunk: a figure's caption would be
          unreadable print, and the body text already says what it shows. */}
      <VisualBlock
        visual={visual}
        compact
        caption={false}
        className='mx-auto max-w-lg'
      />
      <BodyText {...props} />
    </div>
  );
}

function BodyText({
  body,
  className,
  style,
  spacing = 'space-y-3',
}: Omit<BodyContentProps, 'visual'>) {
  // A fenced visual (```smiles / ```funcplot / ```mermaid / …), a table or
  // display maths spans multiple lines, so the line-splitting bullet path
  // would shatter it - render the whole body through the block pipeline.
  if (BLOCK_BODY.test(body)) {
    return (
      <BlockMarkdown
        className={cn(spacing, TYPE.bodyText, LEADING.body, className)}
      >
        {body}
      </BlockMarkdown>
    );
  }

  const lines = body
    .split(/\n+/)
    .map(s => s.trim())
    .filter(Boolean);

  const isBulleted =
    lines.length > 1 && lines.every(line => /^[-•*]\s+/.test(line));

  if (isBulleted) {
    return (
      <ul
        className={cn(
          'list-disc pl-6',
          spacing,
          TYPE.bodyText,
          LEADING.body,
          className
        )}
        style={style}
      >
        {lines.map((line, i) => (
          <li key={i}>
            <InlineMarkdown>{line.replace(/^[-•*]\s+/, '')}</InlineMarkdown>
          </li>
        ))}
      </ul>
    );
  }

  return (
    <div
      className={cn(spacing, TYPE.bodyText, LEADING.body, className)}
      style={style}
    >
      {lines.map((p, i) => (
        <p key={i}>
          <InlineMarkdown>{p}</InlineMarkdown>
        </p>
      ))}
    </div>
  );
}
